import {html, LitElement, nothing} from 'lit';
import {FontSizes, FontWeights} from '../../../types/common/types';
import {customElement, property} from 'lit/decorators.js';
import {styles} from '../view/styles';
import {
  renderCompanyName,
  renderCreatedOn,
  renderLastUpdatedOn,
  renderRuleName,
  renderRuleType,
  renderTags,
} from '../../customRulesGrid/view/CustomGridRenderers';
import {GridType, RuleType, Status} from '../../customRulesGrid/types';
import {translateFn} from '../../../utils/translator.helper';

/**
 * @summary Type here the description of this component
 * @tag custom-rules-general
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('custom-rules-general')
export class CustomRulesGeneralWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: Object, attribute: false, reflect: false })
  rule;

  @property({ type: Object, attribute: false, reflect: false })
  availableCompanies;

  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Boolean })
  isGZ = false;

  @property({ type: Array, attribute: false, reflect: false })
  company;

  @property({ type: Array, attribute: false, reflect: false })
  isPartner;

  @property({ type: Boolean, attribute: false, reflect: false })
  showActions = true;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div>
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
          >${translateFn('General')}
        </gz-title>
        <gz-labeled-content label="${translateFn('Rule name')}">
          ${renderRuleName(this.rule, this, this.company, this.isPartner, this.availableCompanies, this.showActions)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Rule ID')}">${this.renderRuleId()}</gz-labeled-content>
        <gz-labeled-content class="pb-2 wrapped" label="${translateFn('Description')}" stacked
          >${this.renderDescription()}</gz-labeled-content
        >
        ${this.renderTargetsInfo()}
        <gz-labeled-content class="no-margin-container" label="${this.renderStatusLabel()}"
          >${this.renderStatus()}</gz-labeled-content
        >
        <gz-labeled-content label="${translateFn('Rule type')}">${renderRuleType(this.rule)}</gz-labeled-content>
        <gz-labeled-content label="${translateFn('Rule tags')}">${renderTags(this.rule)}</gz-labeled-content>
        <gz-labeled-content label="${translateFn('Created by')}">${this.renderCreatedBy()}</gz-labeled-content>
        <gz-labeled-content label="${translateFn('Created on')}">${renderCreatedOn(this.rule)}</gz-labeled-content>
        <gz-labeled-content label="${translateFn('Last updated on')}"
          >${renderLastUpdatedOn(this.rule)}</gz-labeled-content
        >
        ${this.renderCompanies()}
      </div>
    `;
  }

  private renderTargetsInfo() {
    if (!this.isPartner || this.rule.targets.companyId) {
      return nothing;
    }

    let companies;

    if (this.rule.targets.endpointTags) {
      companies = this.rule.targets.endpointTags.map(item => item.companyId);
    } else {
      companies = this.rule.targets.companiesIds;
    }

    const availableCompanies = this.availableCompanies.map(item => item._id);
    const unavailableCompanies = companies.filter(item => !availableCompanies.includes(item));

    return unavailableCompanies.length === 0
      ? nothing
      : html`
          <gz-info-box .severity="${'informative'}"
            >${translateFn(
              'The rule also applies to targets out of your scope. To edit the rule, contact your Company Administrator. Alternatively, you can create another rule with the same criteria.'
            )}
          </gz-info-box>
        `;

  }

  private renderCompanies() {
    if (!Application.Main.company.isPartner()) {
      return nothing;
    }

    return html`<gz-labeled-content label="${translateFn('Companies')}"
      >${renderCompanyName(this.rule)}</gz-labeled-content
    >`;
  }

  private renderDescription() {
    if (!this.rule.details || this.rule.details.length === 0) {
      return translateFn('No description added yet.');
    }
    return this.rule.details;
  }

  private renderStatusLabel() {
    if (this.gridType === GridType.EXCLUSION) {
      return translateFn('Status');
    }

    return translateFn('On-Access');
  }

  private renderCreatedBy() {
    if (this.isGZ) {
      return this.rule.owner.name;
    }

    const availableCompanies = this.availableCompanies.map(item => item._id);

    if (!availableCompanies.includes(this.rule.companyId) && this.rule.companyId !== this.company.companyId) {
      return translateFn('Partner');
    }

    return this.rule.owner.name;
  }

  private renderStatus() {
    if (
      this.gridType === GridType.DETECTION &&
      this.rule.type === RuleType.YARA &&
      this.rule.status === Status.ENABLED
    ) {
      return html`<div class="d-flex flex-row align-items-center">
        <gz-text class="yara-status w-auto has-bullet bullet-success" fontSize="12" fontWeight="400"
          >${translateFn('Enabled')}</gz-text
        ><gz-chip class="experimental-chip" notselectable text="${translateFn('Experimental')}"></gz-chip>
      </div>`;
    }

    return html`<div class="mb-1">
      <gz-text
        class="yara-status w-auto has-bullet ${this.rule.status === Status.ENABLED
          ? 'bullet-success'
          : 'bullet-neutral'}"
        fontSize="12"
        fontWeight="400"
        >${this.rule.status === Status.ENABLED ? translateFn('Enabled') : translateFn('Disabled')}</gz-text
      >
    </div>`;
  }

  private renderRuleId() {
    return html`<gz-copy-to-clipboard
      confirmationmessage=${translateFn('Copied')}
      tooltiptext=${translateFn('Copy to clipboard')}
    >
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}">${this.rule._id}</gz-text>
    </gz-copy-to-clipboard>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'custom-rules-general': CustomRulesGeneralWebComponent;
  }
}
